import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { SERVICE_KEYS, SERVICE_NUMBERS } from "@/lib/translations";

const SERVICE_LINKS = {
  efficiency: "/leistungen/prozessoptimierung",
  realignment: "/leistungen/defense-security",
  digital: "/leistungen/supply-chain-nearshoring",
  ai_robotics: "/leistungen/industrial-ai-robotics",
};

export default function Services() {
  const { t, dict } = useLang();
  const items = dict.services.items;

  return (
    <section id="services" data-testid="services-section" className="relative py-14 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 mb-16 lg:mb-20">
          <div className="text-center max-w-4xl mx-auto">
            <span className="text-sm uppercase tracking-[0.25em] font-extrabold text-[#1E3A8A]" data-testid="services-eyebrow">
              {t("services.eyebrow")}
            </span>
            <h2 className="mt-6 font-heading font-bold text-[#0A192F] tracking-tight leading-[1.1]">
              <span className="block text-[clamp(1.35rem,5.6vw,3rem)] sm:text-4xl lg:text-5xl">
                {t("services.title")}
              </span>
            </h2>
            <div
              data-testid="services-title-image"
              role="img"
              aria-label="Transforming #TheMittelstand"
              className="-mt-1 lg:-mt-3 -mb-2 lg:-mb-4 w-full max-w-md lg:max-w-2xl mx-auto h-16 sm:h-20 lg:h-24"
              style={{
                backgroundImage:
                  "url('https://customer-assets.emergentagent.com/job_ai-optimize-9/artifacts/2r6y39rq_image.png')",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center 65%",
                backgroundSize: "100% auto",
              }}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-px bg-[#0A192F]/30 border border-[#0A192F]/20 rounded-2xl overflow-hidden">
          {SERVICE_KEYS.map((key, i) => {
            const linkTo = SERVICE_LINKS[key];
            const cardInner = (
              <>
                <div className="flex items-start justify-between mb-10">
                  <span className="font-heading text-2xl lg:text-3xl font-extrabold text-[#C9A96E] group-active:text-[#0A192F] transition-colors leading-none">
                    {SERVICE_NUMBERS[key]}
                  </span>
                  <ArrowUpRight
                    size={32}
                    strokeWidth={2.5}
                    className="text-[#C9A96E] group-hover:text-white group-hover:rotate-45 group-active:text-[#0A192F] transition-all duration-500"
                  />
                </div>
                <h3 className="font-heading text-2xl font-semibold text-white group-active:text-[#0A192F] transition-colors duration-500 whitespace-pre-line leading-[1.15] min-h-[3.5rem]">
                  {items[key].title}
                </h3>
                <div className="mt-4 space-y-3 text-slate-300 group-active:text-slate-700 transition-colors duration-500 text-[15px] leading-relaxed">
                  {items[key].short.split("\n\n").map((para, idx) => (
                    <p key={idx}>{para}</p>
                  ))}
                </div>
                <div className="mt-auto pt-8">
                  <div className="pt-6 border-t border-white/10 group-active:border-slate-200 transition-colors duration-500 flex items-center justify-between gap-3">
                    <span className="text-sm font-bold uppercase tracking-[0.2em] text-[#C9A96E] group-hover:text-[#E91E63] group-active:text-[#E91E63] transition-colors duration-300">
                      {t("services.more")}
                    </span>
                    <span className="w-9 h-9 rounded-full bg-white/5 group-hover:bg-[#E91E63] group-active:bg-[#E91E63] border border-white/10 group-hover:border-[#E91E63] flex items-center justify-center transition-all duration-300">
                      <ArrowUpRight size={16} className="text-[#C9A96E] group-hover:text-white group-active:text-white group-hover:rotate-45 transition-all duration-300" />
                    </span>
                  </div>
                </div>
              </>
            );

            const cardClasses =
              "group relative flex flex-col h-full bg-[#0A192F] p-8 lg:p-10 cursor-pointer transition-all duration-500 hover:bg-[#0F2547] hover:-translate-y-1 active:scale-[0.98] active:bg-white";

            return (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                data-testid={`service-card-${key}`}
                className="h-full"
              >
                {linkTo ? (
                  <Link to={linkTo} className={cardClasses} data-testid={`service-link-${key}`}>
                    {cardInner}
                  </Link>
                ) : (
                  <div className={cardClasses}>{cardInner}</div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
